import { BrowserRouter, Route, Routes, Link, NavLink, Outlet } from 'react-router-dom';
import Dashboard from '../Dashboard/Dashboard';
import Preferences from '../Preferences/Preferences';
import OrganisationsList from '../OrganisationsList/OrganisationsList';
import Help from '../Help/Help';
import Login from '../Login/Login';
import RolfAdmin from '../RolfAdmin/RolfAdmin';
import React, { useReducer, useEffect, useState } from 'react';
import { Context } from '../../context/Context';
import { storeData, getData } from '../../redux/store';
import { useApi } from '../../context/ApiProvider';
import ResetPassword from '../Login/ResetPassword';
import Partners from '../Partners/Partners';
import { LocaleProvider, useLocale } from '../../localization/LocaleProvider';
import LanguageSelect from '../LanguageSelect';

const Clients = React.lazy(() => import("../Clients/Clients"));

function reducer(state, action) {
  let newState;
  switch (action.type) {
    case 'sessionStart':
      newState = { ...state, otp: true, sessionId: action.sessionId }
      storeData('state', newState);
      return newState;
    case 'sessionEnd':
      newState = { ...state, otp: false, sessionId: false, isLoggedIn: false }
      storeData('state', newState);
      return newState;
    case 'login':
      newState = { ...state, isLoggedIn: true, otp: false, sessionId: false }
      storeData('state', newState);
      return newState;
    case 'logout':
      newState = { ...state, isLoggedIn: false }
      storeData('state', newState);
      return newState;
    case 'INIT':
      newState = { ...state, ...action.state }
      storeData('state', newState);
      return newState;
    default:
      throw new Error();
  }
}

function init(defaultValues) {
  return {
    ...defaultValues,
    isLoading: true,
  };
}

function App() {

  const Api = useApi()

  const [state, dispatch] = useReducer(reducer, {}, init);
  const [session, setSession] = useState(false);

  useEffect(() => {
    Promise.all([getData('state'), Api.getToken()]).then(([state, token]) => {
      dispatch({
        type: 'INIT',
        state: {
          ...state,
          isLoggedIn: !!(token?.user?.ID && token?.refreshToken),
          isLoading: false,
        }
      })
    })
  }, []);


  useEffect(() => {
    if (!state.isLoggedIn) {
      setSession(false);
      return;
    }

    Api.getSession().then(session => {
      if (session) {
        Api.get(`/administrators/${session.ID}`).then(user => {
          setSession(user);
        })
      } else {
        setSession(false);
      }
    })
  }, [Api, state.isLoggedIn]);

  const appContext = {
    state,
    dispatch,
  }

  const logout = async () => {
    await Api.clearToken();
    setSession(false);
    appContext.dispatch({ type: 'logout' });
  }


  if (state.isLoading)
    return false;

  if (!state.isLoggedIn) {
    return (<Context.Provider value={appContext}>
      <LocaleProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/*" element={<Login />} />
          <Route path="/resetpass" element={<ResetPassword />} />
        </Routes>
      </BrowserRouter>
      </LocaleProvider>
    </Context.Provider>)
  }

  return (
    <>
      <Context.Provider value={appContext}>
        <LocaleProvider>

        <HeaderComponent logout={logout} />

        <main>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Layout session={session} />}>
                <Route index element={<Dashboard />} />
                <Route path="clients/*" element={<React.Suspense fallback={<p>Loading</p>}><section><Clients /></section></React.Suspense>} />
                <Route path="organisations/*" element={<OrganisationsList />} />
                <Route path="preferences" element={<Preferences />} />
                <Route path="partners/*" element={<Partners />} />
                <Route path="help" element={<Help />} />
                {session.rolf == 1 ?
                  <Route path="rolf/*" element={<RolfAdmin />} />
                  : null}
                <Route path="*" element={<NoMatch />} />

              </Route>
            </Routes>
          </BrowserRouter>
        </main>
        </LocaleProvider>
      </Context.Provider>
    </>
  );
}

function Layout(props) {

  const [session, setSession] = useState(false);
  const { translate } = useLocale(); 

  useEffect(() => {
    setSession(props.session);
  }, [props.session]);

  return (
    <>
      <nav>
        <ul className="nav">
          <li>
            <NavLink to="/" className={({ isActive }) =>
              isActive ? 'active' : undefined
            }>{translate("Home")}</NavLink>
          </li>
          <li>
            <NavLink to="/clients" className={({ isActive }) =>
              isActive ? 'active' : undefined
            }>{translate("Clients")}</NavLink>
          </li>
          <li>
            <NavLink to="/organisations" className={({ isActive }) =>
              isActive ? 'active' : undefined
            }>{translate("Organisations")}</NavLink>
          </li>

          <li>
            <NavLink to="/preferences" className={({ isActive }) =>
              isActive ? 'active' : undefined
            }>{translate("Preferences")}</NavLink>
          </li>
          {session.partnerId ? <li>
            <NavLink to="/partners" className={({ isActive }) =>
              isActive ? 'active' : undefined
            }>Partners</NavLink>
          </li> : null}
          <li>
            <NavLink to="/help" className={({ isActive }) =>
              isActive ? 'active' : undefined
            }>{translate("Help")}</NavLink>
          </li>

          {session.rolf == 1 ?
            <li>
              <NavLink to="/rolf" className={({ isActive }) =>
                isActive ? 'active' : undefined
              }>{translate("Rolf Administration")}</NavLink>
            </li>
            : null
          }

        </ul>
      </nav>
      <div className='container'>
        <div className="content">
          <Outlet />
        </div>
      </div>
    </>
  );
}

const HeaderComponent = (props) => {
  const { translate } = useLocale();

  return (
    <header>
      <p>
        <img src="/images/Rolf_Logo_Inverted_RGB.svg" height="48" alt="Rolf" />
      </p>
      <h2>Administration</h2>
      <div className='flex'>
        <LanguageSelect />
        <input type="button" onClick={props.logout} value={translate("Log out")} />
      </div>
    </header>
  );
}

function NoMatch() {
  return (
    <div>
      <h2>Nothing to see here!</h2>
      <p>
        <Link to="/">Go to the home page</Link>
      </p>
    </div>
  );
}

export default App;
